import { useRef, useState, forwardRef, useImperativeHandle, ForwardedRef, useEffect, useMemo, FormEvent } from 'react';
import { IBAN, BIC } from 'ibankit';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { Button, TypeInput, SelectField, Option, SingleLineText, TitlePart } from '@bloomays-lib/ui.shared';
import { ValidationTextField } from '@bloomays-lib/ui.shared';
import { ExpirationUploadField } from '../molecules/ExpirationUploadField';
import {
  SocietyRegistrationNumberTextFieldMode,
  SocietyRegistrationNumberTextField,
} from '../molecules/SocietyRegistrationNumberTextField';
import path from 'path';
import useCustomLocalStorage from '../../customHooks/useCustomLocalStorage';
import { AddressCountryCodeEU, ISociety } from '@bloomays-lib/types.shared';
import { Logger } from '../../services/serviceLogger';
import VATTextField from '../molecules/VATTextField';
import CountrySelector, { Country } from '../molecules/CountrySelector';
import { addMonthToDate, dateFormat, hasIbanSupport } from '@bloomays-lib/utils.shared';
import { startOfToday } from 'date-fns/startOfToday';
import { isAfter } from 'date-fns/isAfter';

const logger = Logger('EditBloomerSociety');

export type EditBloomerSocietyRef = {
  validate: (e: FormEvent<HTMLFormElement>) => Promise<boolean>;
};

export type EditBloomerSocietyProps = {
  onValidated: (data: ISociety) => Promise<ISociety | undefined>;
  society?: ISociety;
  hideSubmitButton?: boolean;
};

const frenchSocietyStatusOptions: Option[] = [
  { value: 'Auto Entrepreneur', label: '🇫🇷 Auto Entrepreneur', disabled: true },
  { value: 'EI', label: '🇫🇷 Entreprise Individuelle / Micro-entreprise (ex Auto Entrepreneur)' },
  { value: 'EURL', label: '🇫🇷 EURL' },
  { value: 'SARL', label: '🇫🇷 SARL' },
  { value: 'SAS', label: '🇫🇷 SAS' },
  { value: 'SASU', label: '🇫🇷 SASU' },
  { value: 'Autre', label: '🇫🇷 Autre forme Française' },
  { value: 'SUE', label: '🇪🇺 Societé Union Européenne' },
  { value: 'SHUE', label: '🌍 Societé Hors Union Européenne' },
];

const EditBloomerSociety = forwardRef(
  (
    { onValidated, society: bloomerSociety, hideSubmitButton }: EditBloomerSocietyProps,
    ref: ForwardedRef<EditBloomerSocietyRef>,
  ) => {
    const initFile = (currentSociety?: ISociety, property?: string): any => {
      const keyOfSociety = property as keyof ISociety;
      return currentSociety?.[keyOfSociety]
        ? {
            path: path.basename(currentSociety?.[keyOfSociety] as string),
            url: currentSociety?.[keyOfSociety] as string,
          }
        : undefined;
    };

    const isInSocietyOptions = (value: string | undefined, options: Option[]): boolean => {
      return options.some((option) => option.value === value);
    };

    const prepareSociety = (currentSociety?: ISociety) => {
      if (!currentSociety) return;
      return {
        portage: currentSociety.portage || false,
        recordId: currentSociety.recordId,
        name: currentSociety.name,
        siret: currentSociety.siret,
        status: currentSociety.status,
        KBIS: initFile(currentSociety, 'KBIS'),
        KbisExpirationDate: currentSociety.KbisExpirationDate,
        URSSAFVigilanceCertificate: initFile(currentSociety, 'URSSAFVigilanceCertificate'),
        URSSAFVCExpirationDate: currentSociety.URSSAFVCExpirationDate,
        RCPVigilanceCertificate: initFile(currentSociety, 'RCPVigilanceCertificate'),
        RCPExpirationDate: currentSociety.RCPExpirationDate,
        IBAN: currentSociety.IBAN,
        BIC: currentSociety.BIC,
        tva: currentSociety.tva,
        addressCountryCode: currentSociety.addressCountryCode,
      };
    };

    const LS_EXPIRATION_DATES_KEY = 'bloomer-onboarding-expiration-dates';

    const UploadKBISRef = useRef<any>(null);
    const UploadURSSAFRef = useRef<any>(null);
    const UploadRCPRef = useRef<any>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [KBIS, setKBIS] = useState<any | undefined | null>(initFile(bloomerSociety, 'KBIS'));
    const [URSSAF, setURSSAF] = useState<any | undefined | null>(
      initFile(bloomerSociety, 'URSSAFVigilanceCertificate'),
    );
    const [RCP, setRCP] = useState<any | undefined | null>(initFile(bloomerSociety, 'RCPVigilanceCertificate'));

    const [KbisExpirationDate, setKbisExpirationDate] = useState<string | undefined>(
      bloomerSociety ? bloomerSociety.KbisExpirationDate : undefined,
    );
    const [URSSAFVCExpirationDate, setURSSAFVCExpirationDate] = useState<string | undefined>(
      bloomerSociety ? bloomerSociety.URSSAFVCExpirationDate : undefined,
    );
    const [RCPExpirationDate, setRCPVCExpirationDate] = useState<string | undefined>(
      bloomerSociety ? bloomerSociety.RCPExpirationDate : undefined,
    );
    const [formSociety, setFormSociety] = useCustomLocalStorage<ISociety | undefined>(
      bloomerSociety?.recordId ? `bloomerSociety-${bloomerSociety.recordId}` : 'bloomerSociety',
      prepareSociety(bloomerSociety),
    );
    const [onboardingDraftDates, setOnboardingDraftDates] = useCustomLocalStorage<
      { KbisExpirationDate?: string; URSSAFVCExpirationDate?: string; RCPExpirationDate?: string } | undefined
    >(LS_EXPIRATION_DATES_KEY, undefined);
    const [expirationDateErrors, setExpirationDateErrors] = useState<{
      kbis?: string;
      urssaf?: string;
      rcp?: string;
    }>({});

    const supportIban = useMemo(() => {
      return hasIbanSupport(formSociety?.status, formSociety?.addressCountryCode || undefined);
    }, [formSociety?.status, formSociety?.addressCountryCode]);

    useEffect(() => {
      setFormSociety(prepareSociety(bloomerSociety));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bloomerSociety]);

    const isFirstTime = !bloomerSociety?.recordId;
    useEffect(() => {
      if (isFirstTime && onboardingDraftDates) {
        if (onboardingDraftDates.KbisExpirationDate) setKbisExpirationDate(onboardingDraftDates.KbisExpirationDate);
        if (onboardingDraftDates.URSSAFVCExpirationDate)
          setURSSAFVCExpirationDate(onboardingDraftDates.URSSAFVCExpirationDate);
        if (onboardingDraftDates.RCPExpirationDate) setRCPVCExpirationDate(onboardingDraftDates.RCPExpirationDate);
      }
    }, [isFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

    logger.debug('bloomerSociety', bloomerSociety);
    logger.debug('society', formSociety);
    const [preventText, setPreventText] = useState(false);
    const statuts: Option[] = frenchSocietyStatusOptions;
    const statutsPortage = useMemo(() => {
      return frenchSocietyStatusOptions.slice(2);
    }, []);

    const { t } = useTranslation(['bloomer', 'mission']);

    const {
      isMicroEntrepreneur,
      societyKBISLabel,
      requiredVATNumber,
      showVATTextField,
      fieldMode,
      kbisRequired,
      urssafRequired,
      societyTaxesLabel,
      isForeignSociety,
      isEuropeanSociety,
    } = useMemo((): {
      isMicroEntrepreneur: boolean;
      isEuropeanSociety: boolean;
      isForeignSociety: boolean;
      societyKBISLabel: string;
      requiredVATNumber: boolean;
      showVATTextField: boolean;
      fieldMode: SocietyRegistrationNumberTextFieldMode;
      kbisRequired: boolean;
      urssafRequired: boolean;
      societyTaxesLabel: string;
    } => {
      let fieldMode: SocietyRegistrationNumberTextFieldMode;
      let isMicroEntrepreneur;
      let isEuropeanSociety;
      let isForeignSociety;
      let requiredVATNumber;
      let showVATTextField;
      let kbisRequired = true;
      switch (formSociety?.status) {
        case 'Auto Entrepreneur':
        case 'EI':
          isEuropeanSociety = false;
          isMicroEntrepreneur = true;
          requiredVATNumber = false;
          isForeignSociety = false;
          showVATTextField = true;
          fieldMode = 'siret';
          break;
        case 'SUE':
          isEuropeanSociety = true;
          isMicroEntrepreneur = false;
          requiredVATNumber = false;
          showVATTextField = false;
          fieldMode = 'european';
          isForeignSociety = false;
          break;
        case 'SHUE':
          isEuropeanSociety = false;
          isMicroEntrepreneur = false;
          isForeignSociety = true;
          requiredVATNumber = false;
          showVATTextField = false;
          fieldMode = 'foreign';
          isForeignSociety = true;
          break;
        default:
          requiredVATNumber = true;
          fieldMode = 'siret';
          isMicroEntrepreneur = false;
          isEuropeanSociety = false;
          isForeignSociety = false;
          requiredVATNumber = true;
          showVATTextField = true;
          isForeignSociety = false;
          break;
      }
      const societyKBISLabel =
        isEuropeanSociety || isForeignSociety
          ? "certificat de création d'entreprise"
          : formSociety?.portage || !isMicroEntrepreneur
          ? 'KBIS'
          : 'KBIS, attestation Sirene, INSEE';
      const societyTaxesLabel =
        isEuropeanSociety || isForeignSociety || isMicroEntrepreneur
          ? 'attestation de réglement des impôts et taxes locales'
          : 'Certificat Vigilance URSSAF';
      const urssafRequired = formSociety?.portage !== true && !isMicroEntrepreneur;
      kbisRequired = formSociety?.portage ? false : true;
      return {
        isMicroEntrepreneur,
        isEuropeanSociety,
        isForeignSociety,
        societyKBISLabel,
        requiredVATNumber,
        showVATTextField,
        fieldMode,
        kbisRequired,
        urssafRequired,
        societyTaxesLabel,
      };
    }, [formSociety?.status, formSociety?.portage]);

    const hasKbisDoc = Boolean(KBIS || formSociety?.KBIS);
    const hasUrssafDoc = Boolean(URSSAF || formSociety?.URSSAFVigilanceCertificate);
    const hasRcpDoc = Boolean(RCP || formSociety?.RCPVigilanceCertificate);

    const mustHaveKbisExpiration = kbisRequired || hasKbisDoc;
    const mustHaveUrssafExpiration = urssafRequired || hasUrssafDoc;
    const mustHaveRcpExpiration = hasRcpDoc;

    useImperativeHandle(ref, () => ({
      validate(e: any) {
        return onSubmit(e);
      },
    }));

    const shouldDisableExpirationDate = (date: Date) => {
      return isAfter(startOfToday(), date);
    };

    const onSubmit = async (e: { preventDefault: () => void }) => {
      logger.debug('onSubmit');
      e.preventDefault();
      if (!UploadKBISRef.current.validate()) {
        UploadKBISRef.current?.scrollIntoView();
        return false;
      }
      if (!UploadURSSAFRef.current.validate()) {
        UploadURSSAFRef.current?.scrollIntoView();
        return false;
      }
      if (!UploadRCPRef.current.validate()) {
        UploadRCPRef.current?.scrollIntoView();
        return false;
      }
      if (mustHaveKbisExpiration && !KbisExpirationDate) {
        logger.warn('Validation failed: KBIS expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, kbis: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
      if (mustHaveUrssafExpiration && !URSSAFVCExpirationDate) {
        logger.warn('Validation failed: URSSAF expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, urssaf: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
      if (mustHaveRcpExpiration && !RCPExpirationDate) {
        logger.warn('Validation failed: RCP expiration date missing');
        setExpirationDateErrors((prev) => ({ ...prev, rcp: t('expirationDateMustBeInFuture', { ns: 'bloomer' }) }));
        return false;
      }
      setExpirationDateErrors({});
      try {
        if (formSociety) {
          formSociety.KBIS = KBIS;
          formSociety.URSSAFVigilanceCertificate = URSSAF;
          formSociety.RCPVigilanceCertificate = RCP;
          formSociety.URSSAFVCExpirationDate = URSSAFVCExpirationDate;
          formSociety.KbisExpirationDate = KbisExpirationDate;
          formSociety.RCPExpirationDate = RCPExpirationDate;
          setUploading(true);
          if (!isForeignSociety && !isEuropeanSociety) {
            formSociety.addressCountryCode = 'FR';
          }
          if (isEuropeanSociety && !formSociety.tva) {
            formSociety.tva = formSociety.siret;
          }
          const updatedSociety = await onValidated(formSociety);
          if (updatedSociety) {
            setFormSociety(prepareSociety(updatedSociety));
            if (isFirstTime) {
              try {
                // clean onboarding expiration dates localStorage
                setOnboardingDraftDates(undefined);
              } catch (error) {
                logger.error('Error removing onboarding expiration dates', { updatedSociety }, error as Error);
              }
              setOnboardingDraftDates(undefined);
              logger.info('Society saved with expiration dates');
            }
          }
          setUploading(false);
          setPreventText(false);
        }
      } catch (err) {
        logger.error('onSubmit error', { formSociety, error: err }, err as Error);
        setUploading(false);
      }
      return true;
    };

    const selectSocietyOption = formSociety?.portage ? statutsPortage : statuts;

    return (
      <Container>
        <TitlePart textTitle="À propos de qui va nous envoyer" span="ta facture" />
        <ValidationTextField
          aria-label="bloomersocietyinfo-name"
          id="societyName"
          name="societyName"
          label="Nom"
          value={formSociety?.name || ''}
          type={TypeInput.text}
          required={true}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFormSociety({
              ...formSociety,
              name: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        <SelectField
          data-testid="portage"
          aria-label="bloomersocietyinfo-portage"
          id="societyPortage"
          name="societyPortage"
          label="C'est une société de portage ?"
          defaultOption={true}
          value={formSociety?.portage === undefined ? '' : formSociety?.portage ? '1' : '0'}
          required={true}
          options={[
            { value: '0', label: 'Non' },
            { value: '1', label: 'Oui' },
          ]}
          handlechange={(e) => {
            e.preventDefault();
            const isPortage = e.target.value === '1';
            if (isMicroEntrepreneur && isPortage) {
              setFormSociety({
                ...formSociety,
                status: undefined,
                portage: isPortage,
              } as ISociety);
            } else {
              setFormSociety({
                ...formSociety,
                portage: isPortage,
              } as ISociety);
            }
          }}
        />
        <SelectField
          data-testid="societyStatus"
          aria-label="bloomersocietyinfo-status"
          id="societyStatus"
          name="societyStatus"
          label="Status (forme juridique)"
          defaultOption={true}
          value={isInSocietyOptions(formSociety?.status, selectSocietyOption) ? formSociety?.status : ''}
          required={true}
          options={selectSocietyOption}
          handlechange={(e) => {
            e.preventDefault();
            setFormSociety({
              ...formSociety,
              status: e.target.value,
            } as ISociety);
          }}
        />
        <CountrySelector
          id="societyCountry"
          name="societyCountry"
          value={formSociety?.addressCountryCode || undefined}
          required={true}
          onChange={(e?: Country) => {
            setFormSociety({
              ...formSociety,
              addressCountryCode: e?.iso2,
            } as ISociety);
          }}
          allowedCountryCode={(code: string) => {
            if (isForeignSociety) {
              return code !== 'FR' && !AddressCountryCodeEU.includes(code);
            } else {
              if (isEuropeanSociety) {
                return code !== 'FR' && AddressCountryCodeEU.includes(code);
              }
              return code === 'FR';
            }
          }}
          hidden={!(isForeignSociety || isEuropeanSociety)}
        />
        <SocietyRegistrationNumberTextField
          fieldMode={fieldMode}
          aria-label="bloomersocietyinfo-siret"
          id="societySiret"
          name={formSociety?.siret}
          required={true}
          value={formSociety?.siret || ''}
          onChange={(e) => {
            if (e.target.value !== bloomerSociety?.siret && bloomerSociety?.siret) {
              setFormSociety((ps) => {
                return {
                  ...(ps || {}),
                  siret: e.target.value,
                  portage: undefined,
                  name: '',
                  status: '',
                  IBAN: '',
                  BIC: '',
                  tva: '',
                };
              });
              setKBIS(null);
              setURSSAF(null);
              setRCP(null);
              setPreventText(true);
            } else {
              setFormSociety({
                ...formSociety,
                KBIS: undefined,
                siret: e.target.value,
              } as ISociety);
              setPreventText(false);
            }
          }}
          formElement={true}
        />
        {preventText && (
          <SingleLineText
            text="Attention en changeant le siret, tu t'apprêtes à créer une nouvelle société !"
            sx={{ paddingBottom: '1em', color: 'red' }}
          />
        )}
        <VATTextField
          aria-label="bloomersocietyinfo-vat"
          id="societySiret"
          name={formSociety?.tva}
          hidden={!showVATTextField}
          required={requiredVATNumber}
          value={formSociety?.tva || ''}
          onChange={(e) => {
            setFormSociety({
              ...formSociety,
              KBIS: undefined,
              tva: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        {isMicroEntrepreneur && (
          <SingleLineText
            text="Le numéro de TVA est obligatoire si assujeti à la TVA pour traiter vos factures."
            sx={{ paddingBottom: '1em' }}
          />
        )}
        <ValidationTextField
          aria-label="bloomersocietyinfo-iban"
          id="societyIban"
          name={formSociety?.IBAN}
          label={
            supportIban
              ? t('ibanLabel', { ns: 'mission' })
              : t('ibanFallbackLabel', {
                  ns: 'mission',
                })
          }
          value={formSociety?.IBAN || ''}
          type={TypeInput.text}
          placeholder={supportIban ? 'FR12 1234 1234 1234 1234 1234 12' : 'XXXXXXXX'}
          hidden={formSociety?.portage}
          required={!formSociety?.portage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (supportIban && !IBAN.isValid((e.target as HTMLInputElement).value)) {
              (e.target as HTMLInputElement).setCustomValidity('IBAN mal formé');
            } else {
              (e.target as HTMLInputElement).setCustomValidity('');
            }
            setFormSociety({
              ...formSociety,
              IBAN: (e.target as HTMLInputElement).value,
            } as ISociety);
          }}
          formElement={true}
        />
        <ValidationTextField
          aria-label="bloomersocietyinfo-bic"
          id="societyBic"
          name={formSociety?.BIC}
          label={
            supportIban
              ? t('bicLabel', { ns: 'mission' })
              : t('bicFallbackLabel', {
                  ns: 'mission',
                })
          }
          value={formSociety?.BIC || ''}
          type={TypeInput.text}
          minLength={'8'}
          maxLength={'11'}
          hidden={formSociety?.portage}
          placeholder="8 à 11 caractères"
          required={!formSociety?.portage}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!BIC.isValid(e.target.value)) {
              e.target.setCustomValidity(supportIban ? 'BIC' : 'SWIFT / BIC');
            } else {
              e.target.setCustomValidity('');
            }
            setFormSociety({
              ...formSociety,
              BIC: e.target.value,
            } as ISociety);
          }}
          formElement={true}
        />
        <ExpirationUploadField
          title={societyKBISLabel}
          uploadAriaLabel="bloomersocietyinfo-kbis"
          uploadId="KBIS"
          uploadName="KBIS"
          uploadPlaceholder={societyKBISLabel}
          uploadTextLabel="Ajouter"
          uploadRequired={kbisRequired}
          preloadedFiles={KBIS ? [KBIS] : []}
          onUploadChange={(files) => {
            setKBIS(files.length ? files[0] : null);
            setKbisExpirationDate(undefined);
          }}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadKBISRef}
          expirationLabel={mustHaveKbisExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={KbisExpirationDate ? new Date(KbisExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setKbisExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, kbis: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, KbisExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.kbis}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, kbis: undefined }))}
          shouldDisableDate={shouldDisableExpirationDate}
          minDate={new Date()}
          maxDate={addMonthToDate(new Date(), 3)}
        />
        {preventText && formSociety?.recordId && (
          <SingleLineText
            text="Tu t'apprêtes à créer une nouvelle société, tu dois donc uploader un nouveau KBIS / attestation sirene / INSEE"
            sx={{ paddingBottom: '1em' }}
          />
        )}
        <ExpirationUploadField
          title={societyTaxesLabel}
          uploadAriaLabel="bloomersocietyinfo-urssaf"
          uploadId="URSSAFVigilanceCertificate"
          uploadName="URSSAFVigilanceCertificate"
          uploadPlaceholder={societyTaxesLabel}
          uploadTextLabel="Ajouter"
          uploadRequired={urssafRequired}
          preloadedFiles={URSSAF ? [URSSAF] : []}
          onUploadChange={(files) => {
            setURSSAF(files.length ? files[0] : null);
            setURSSAFVCExpirationDate(undefined);
          }}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadURSSAFRef}
          expirationLabel={mustHaveUrssafExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={URSSAFVCExpirationDate ? new Date(URSSAFVCExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setURSSAFVCExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, urssaf: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, URSSAFVCExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.urssaf}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, urssaf: undefined }))}
          shouldDisableDate={shouldDisableExpirationDate}
          minDate={new Date()}
          maxDate={addMonthToDate(new Date(), 6)}
        />
        <ExpirationUploadField
          title="Certificat Vigilance RCP"
          uploadAriaLabel="bloomersocietyinfo-rcp"
          uploadId="RCPVigilanceCertificate"
          uploadName="RCPVigilanceCertificate"
          uploadPlaceholder="Certificat Vigilance RCP"
          uploadTextLabel="Ajouter"
          uploadRequired={false}
          preloadedFiles={RCP ? [RCP] : []}
          onUploadChange={(files) => setRCP(files.length ? files[0] : null)}
          fileTypes={['.pdf']}
          maxFiles={1}
          maxLength={5000000}
          uploadAutoload={true}
          uploadDisplayIcons={true}
          uploadRef={UploadRCPRef}
          expirationLabel={mustHaveRcpExpiration ? `${t('expirationDate')} *` : t('expirationDate')}
          expirationValue={RCPExpirationDate ? new Date(RCPExpirationDate) : undefined}
          onExpirationChange={(iso: string) => {
            const value = dateFormat(new Date(iso), 'yyyy-MM-dd');
            setRCPVCExpirationDate(value);
            setExpirationDateErrors((prev) => ({ ...prev, rcp: undefined }));
            if (isFirstTime) {
              setOnboardingDraftDates((prev) => ({ ...prev, RCPExpirationDate: value }));
            }
          }}
          expirationError={expirationDateErrors.rcp}
          onExpirationErrorClear={() => setExpirationDateErrors((prev) => ({ ...prev, rcp: undefined }))}
          shouldDisableDate={shouldDisableExpirationDate}
          minDate={new Date()}
          infoContent={
            "RCP, c'est quoi ? 👉 Votre entreprise peut causer dans le cadre de son activité un préjudice à un tiers. Ce dernier relève de la Responsabilité Civile Professionnelle  de votre entreprise. L’assurance RC Pro couvre alors votre entreprise en remboursant à la victime concernée tout ou partie des dommages occasionnés."
          }
          maxDate={addMonthToDate(new Date(), 12)}
        />
        {!hideSubmitButton && (
          <Button
            buttonType="submit"
            // eslint-disable-next-line jsx-a11y/aria-role
            role={'button-editbloomersociety'}
            none={'none'}
            textButton={uploading ? "En cours d'envoi" : 'Sauvegarder mes données'}
            disable={uploading}
          />
        )}
      </Container>
    ) as React.ReactNode;
  },
);

export default EditBloomerSociety;

export { EditBloomerSociety };
EditBloomerSociety.displayName = 'EditBloomerSociety';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  backgroundColor: theme.palette.neutral.white,
  boxShadow: '2px 2px 20px lightgrey',
  borderRadius: '10px',
  width: '100%',
  margin: '1em auto',
  padding: '1.5em',
}));
